import useSWR from 'swr';
import api from '../../services/api';

export interface EmailAccount {
  id: number;
  emailAddress: string;
  displayName?: string;
  incomingProtocol?: 'imap' | 'pop3';
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  imapUsername?: string | null;
  imapPassword?: string | null;
  pop3Host?: string | null;
  pop3Port?: number | null;
  pop3Secure?: boolean | null;
  pop3Username?: string | null;
  pop3Password?: string | null;
  pop3DeleteAfterDownload?: boolean;
  pop3RetentionDays?: number | null;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  username: string;
  syncEnabled: boolean;
  syncInterval: number;
  /** IMAP: single_folder (INBOX cron) or full_mailbox (all selectable folders) */
  imapMirrorMode?: string;
  /** Default for shared users: show under Accounts in /emails */
  showInAccountsSidebar?: boolean;
  /** Auto-create INBOX search folder */
  showSearchFolderInbox?: boolean;
  showSentFolder?: boolean;
  oauthProvider?: string | null;
  showMySentFolder?: boolean;
  /** Other account IDs whose Sent / My sent are merged into this mailbox */
  sentSourceAccountIds?: number[] | null;
  /** Send-as identity IDs for Sent / My sent (0 = default address). Null = all. */
  sentFolderIdentityIds?: number[] | null;
  lastSyncAt?: string;
  syncStatus?: string;
  syncError?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    id: number;
    userEmail: string;
    userDisplayName?: string;
  };
  _count?: {
    emails: number;
    sendIdentities?: number;
    shares?: number;
  };
}

export interface EmailAccountSendIdentity {
  id: number;
  emailAccountId: number;
  name: string;
  displayName: string | null;
  emailAddress: string | null;
  replyTo: string | null;
  isDefault: boolean;
  displayOrder: number;
}

export interface EmailAccountSendIdentitiesResponse {
  byAccount: Record<
    number,
    { emailAddress: string; displayName?: string | null; identities: EmailAccountSendIdentity[] }
  >;
  accounts: Array<{ id: number; emailAddress: string; displayName?: string | null }>;
  identities: EmailAccountSendIdentity[];
}

export const useEmailAccounts = (searchParams?: string) => {
  const { data, error, isLoading, mutate } = useSWR<EmailAccount[]>(
    `/email-accounts/manage${searchParams ? `?${searchParams}` : ''}`,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useEmailAccountSendIdentities = () => {
  const { data, error, isLoading, mutate } = useSWR<EmailAccountSendIdentitiesResponse>(
    '/emails/send-identities/list',
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    }
  );

  return {
    data,
    byAccount: data?.byAccount ?? {},
    isLoading,
    isError: error,
    mutate,
  };
};

export const useEmailAccount = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<EmailAccount>(
    id ? `/email-accounts/${id}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    }
  );

  return {
    data,
    isLoading,
    isError: error,
    mutate,
  };
};
